import React, { useState } from 'react';
import { XIcon, CheckCircleIcon, PackageIcon, TruckIcon, CreditCardIcon, ArrowLeftIcon, LockIcon } from 'lucide-react';
import { ShoppingBagItem } from './ShoppingBag';
interface CheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  items: ShoppingBagItem[];
  onOrderComplete: () => void;
}
const BASE_PRICE = 29.99;
const TAX_RATE = 0.08; // 8% tax
const SHIPPING_COST = 9.99;
type CheckoutStep = 'review' | 'shipping' | 'payment' | 'confirmation';
export function Checkout({
  isOpen,
  onClose,
  items,
  onOrderComplete
}: CheckoutProps) {
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('review');
  const [shippingInfo, setShippingInfo] = useState({
    fullName: 'John Doe',
    email: 'john.doe@example.com',
    address: '123 Main Street',
    city: 'San Francisco',
    state: 'CA',
    zipCode: '94102',
    country: 'United States'
  });
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '4242 4242 4242 4242',
    expiryDate: '12/25',
    cvv: '123',
    cardholderName: 'John Doe'
  });
  const [orderNumber, setOrderNumber] = useState('');
  const [cardFocused, setCardFocused] = useState<string | null>(null);
  if (!isOpen) return null;
  const subtotal = items.reduce((sum, item) => {
    const itemTotal = Object.values(item.sizeQuantities).reduce((s, q) => s + q, 0);
    return sum + itemTotal * BASE_PRICE;
  }, 0);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax + SHIPPING_COST;
  const totalItems = items.reduce((sum, item) => {
    return sum + Object.values(item.sizeQuantities).reduce((s, q) => s + q, 0);
  }, 0);
  const isShippingValid = shippingInfo.fullName.trim() !== '' && shippingInfo.email.trim() !== '' && shippingInfo.address.trim() !== '' && shippingInfo.city.trim() !== '' && shippingInfo.state.trim() !== '' && shippingInfo.zipCode.trim() !== '';
  const isPaymentValid = paymentInfo.cardNumber.replace(/\s/g, '').length === 16 && paymentInfo.expiryDate.trim() !== '' && paymentInfo.cvv.length === 3 && paymentInfo.cardholderName.trim() !== '';
  const handlePlaceOrder = () => {
    const orderNum = `MP-${Date.now().toString().slice(-8)}`;
    setOrderNumber(orderNum);
    setCurrentStep('confirmation');
  };
  const handleClose = () => {
    if (currentStep === 'confirmation') {
      onOrderComplete();
    }
    setCurrentStep('review');
    onClose();
  };
  const getCardBrand = (number: string) => {
    const cleaned = number.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return 'Visa';
    if (cleaned.startsWith('5')) return 'Mastercard';
    if (cleaned.startsWith('3')) return 'Amex';
    return 'Card';
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-90">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-91">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-92">
          <div className="flex items-center gap-3" data-id="element-93">
            {currentStep !== 'confirmation' && currentStep !== 'review' && <button onClick={() => {
            if (currentStep === 'shipping') setCurrentStep('review');
            if (currentStep === 'payment') setCurrentStep('shipping');
          }} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-94">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-95" />
              </button>}
            <div data-id="element-96">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-97">
                {currentStep === 'review' && 'Review Order'}
                {currentStep === 'shipping' && 'Shipping Information'}
                {currentStep === 'payment' && 'Payment Details'}
                {currentStep === 'confirmation' && 'Order Confirmed!'}
              </h3>
              {currentStep !== 'confirmation' && <p className="text-sm text-gray-500" data-id="element-98">
                  Step{' '}
                  {currentStep === 'review' ? '1' : currentStep === 'shipping' ? '2' : '3'}{' '}
                  of 3
                </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-99">
            <XIcon className="w-5 h-5" data-id="element-100" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-101">
          {/* Step 1: Review Order */}
          {currentStep === 'review' && <div className="space-y-6" data-id="element-102">
              <div data-id="element-103">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-104">
                  <PackageIcon className="w-4 h-4" data-id="element-105" />
                  Order Summary
                </h4>
                <div className="space-y-3" data-id="element-106">
                  {items.map(item => {
                const itemTotal = Object.values(item.sizeQuantities).reduce((s, q) => s + q, 0);
                const itemPrice = itemTotal * BASE_PRICE;
                return <div key={item.id} className="border border-gray-200 rounded-lg p-4" data-id="element-107">
                        <div className="flex gap-4" data-id="element-108">
                          <img src={item.thumbnail} alt={item.name} className="w-16 h-16 object-cover rounded-lg bg-gray-50" data-id="element-109" />
                          <div className="flex-1" data-id="element-110">
                            <h5 className="text-sm font-semibold text-gray-900" data-id="element-111">
                              {item.name}
                            </h5>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-112">
                              {item.colorName} {item.productName}
                            </p>
                            <div className="mt-2 text-xs text-gray-600" data-id="element-113">
                              {Object.entries(item.sizeQuantities).filter(([_, qty]) => qty > 0).map(([size, qty]) => <span key={size} className="inline-block mr-3" data-id="element-114">
                                    {size}: {qty}×
                                  </span>)}
                            </div>
                          </div>
                          <div className="text-right" data-id="element-115">
                            <p className="text-sm font-semibold text-gray-900" data-id="element-116">
                              ${itemPrice.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-117">
                              {itemTotal} items
                            </p>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4" data-id="element-118">
                <div className="space-y-2" data-id="element-119">
                  <div className="flex justify-between text-sm" data-id="element-120">
                    <span className="text-gray-600" data-id="element-121">
                      Subtotal ({totalItems} items)
                    </span>
                    <span className="text-gray-900" data-id="element-122">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-123">
                    <span className="text-gray-600" data-id="element-124">Shipping</span>
                    <span className="text-gray-900" data-id="element-125">
                      ${SHIPPING_COST.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-126">
                    <span className="text-gray-600" data-id="element-127">Tax (8%)</span>
                    <span className="text-gray-900" data-id="element-128">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200" data-id="element-129">
                    <span className="text-gray-900" data-id="element-130">Total</span>
                    <span className="text-gray-900" data-id="element-131">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>}

          {/* Step 2: Shipping Information */}
          {currentStep === 'shipping' && <div className="space-y-4" data-id="element-132">
              <div className="flex items-center gap-2 mb-4" data-id="element-133">
                <TruckIcon className="w-5 h-5 text-blue-600" data-id="element-134" />
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-135">
                  Shipping Address
                </h4>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-136">
                <p className="text-xs text-blue-800" data-id="element-137">
                  💡 <strong data-id="element-138">Test Mode:</strong> Form is pre-filled with default
                  values for quick testing
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-139">
                <div className="col-span-2" data-id="element-140">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-141">
                    Full Name *
                  </label>
                  <input type="text" value={shippingInfo.fullName} onChange={e => setShippingInfo({
                ...shippingInfo,
                fullName: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="John Doe" data-id="element-142" />
                </div>

                <div className="col-span-2" data-id="element-143">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-144">
                    Email Address *
                  </label>
                  <input type="email" value={shippingInfo.email} onChange={e => setShippingInfo({
                ...shippingInfo,
                email: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="john@example.com" data-id="element-145" />
                </div>

                <div className="col-span-2" data-id="element-146">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-147">
                    Street Address *
                  </label>
                  <input type="text" value={shippingInfo.address} onChange={e => setShippingInfo({
                ...shippingInfo,
                address: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="123 Main St" data-id="element-148" />
                </div>

                <div data-id="element-149">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-150">
                    City *
                  </label>
                  <input type="text" value={shippingInfo.city} onChange={e => setShippingInfo({
                ...shippingInfo,
                city: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="San Francisco" data-id="element-151" />
                </div>

                <div data-id="element-152">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-153">
                    State *
                  </label>
                  <input type="text" value={shippingInfo.state} onChange={e => setShippingInfo({
                ...shippingInfo,
                state: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="CA" data-id="element-154" />
                </div>

                <div data-id="element-155">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-156">
                    ZIP Code *
                  </label>
                  <input type="text" value={shippingInfo.zipCode} onChange={e => setShippingInfo({
                ...shippingInfo,
                zipCode: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="94102" data-id="element-157" />
                </div>

                <div data-id="element-158">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-159">
                    Country *
                  </label>
                  <input type="text" value={shippingInfo.country} onChange={e => setShippingInfo({
                ...shippingInfo,
                country: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-160" />
                </div>
              </div>
            </div>}

          {/* Step 3: Payment - Stripe Style */}
          {currentStep === 'payment' && <div className="space-y-6" data-id="element-161">
              <div className="flex items-center justify-between mb-4" data-id="element-162">
                <div className="flex items-center gap-2" data-id="element-163">
                  <CreditCardIcon className="w-5 h-5 text-indigo-600" data-id="element-164" />
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-165">
                    Payment Information
                  </h4>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500" data-id="element-166">
                  <LockIcon className="w-3 h-3" data-id="element-167" />
                  <span data-id="element-168">Secured by Stripe</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-169">
                <p className="text-xs text-blue-800" data-id="element-170">
                  💡 <strong data-id="element-171">Test Mode:</strong> Using test card 4242 4242 4242
                  4242
                </p>
              </div>

              {/* Stripe-style Card Input */}
              <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-172">
                <div className="space-y-4" data-id="element-173">
                  {/* Card Number */}
                  <div data-id="element-174">
                    <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-175">
                      Card number
                    </label>
                    <div className="relative" data-id="element-176">
                      <input type="text" value={paymentInfo.cardNumber} onChange={e => {
                    const value = e.target.value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
                    setPaymentInfo({
                      ...paymentInfo,
                      cardNumber: value
                    });
                  }} onFocus={() => setCardFocused('number')} onBlur={() => setCardFocused(null)} maxLength={19} className={`w-full px-3 py-3 border-0 focus:outline-none text-base ${cardFocused === 'number' ? 'bg-blue-50' : 'bg-transparent'}`} placeholder="1234 1234 1234 1234" data-id="element-177" />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500" data-id="element-178">
                        {getCardBrand(paymentInfo.cardNumber)}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200" data-id="element-179"></div>

                  {/* Expiry and CVC */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-180">
                    <div data-id="element-181">
                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-182">
                        Expiration
                      </label>
                      <input type="text" value={paymentInfo.expiryDate} onChange={e => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length >= 2) {
                      value = value.slice(0, 2) + '/' + value.slice(2, 4);
                    }
                    setPaymentInfo({
                      ...paymentInfo,
                      expiryDate: value
                    });
                  }} onFocus={() => setCardFocused('expiry')} onBlur={() => setCardFocused(null)} className={`w-full px-3 py-3 border-0 focus:outline-none text-base ${cardFocused === 'expiry' ? 'bg-blue-50' : 'bg-transparent'}`} placeholder="MM/YY" maxLength={5} data-id="element-183" />
                    </div>

                    <div data-id="element-184">
                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-185">
                        CVC
                      </label>
                      <input type="text" value={paymentInfo.cvv} onChange={e => setPaymentInfo({
                    ...paymentInfo,
                    cvv: e.target.value.replace(/\D/g, '')
                  })} onFocus={() => setCardFocused('cvc')} onBlur={() => setCardFocused(null)} maxLength={3} className={`w-full px-3 py-3 border-0 focus:outline-none text-base ${cardFocused === 'cvc' ? 'bg-blue-50' : 'bg-transparent'}`} placeholder="123" data-id="element-186" />
                    </div>
                  </div>

                  <div className="border-t border-gray-200" data-id="element-187"></div>

                  {/* Cardholder Name */}
                  <div data-id="element-188">
                    <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-189">
                      Cardholder name
                    </label>
                    <input type="text" value={paymentInfo.cardholderName} onChange={e => setPaymentInfo({
                  ...paymentInfo,
                  cardholderName: e.target.value
                })} onFocus={() => setCardFocused('name')} onBlur={() => setCardFocused(null)} className={`w-full px-3 py-3 border-0 focus:outline-none text-base ${cardFocused === 'name' ? 'bg-blue-50' : 'bg-transparent'}`} placeholder="John Doe" data-id="element-190" />
                  </div>
                </div>
              </div>

              {/* Stripe Branding */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-191">
                <LockIcon className="w-3 h-3" data-id="element-192" />
                <span data-id="element-193">Powered by</span>
                <span className="font-semibold text-indigo-600" data-id="element-194">Stripe</span>
              </div>

              {/* Order Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-195">
                <h5 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-196">
                  Order Total
                </h5>
                <div className="space-y-2" data-id="element-197">
                  <div className="flex justify-between text-sm" data-id="element-198">
                    <span className="text-gray-600" data-id="element-199">Subtotal</span>
                    <span className="text-gray-900" data-id="element-200">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-201">
                    <span className="text-gray-600" data-id="element-202">Shipping</span>
                    <span className="text-gray-900" data-id="element-203">
                      ${SHIPPING_COST.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-204">
                    <span className="text-gray-600" data-id="element-205">Tax</span>
                    <span className="text-gray-900" data-id="element-206">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200" data-id="element-207">
                    <span className="text-gray-900" data-id="element-208">Total</span>
                    <span className="text-gray-900" data-id="element-209">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>}

          {/* Confirmation */}
          {currentStep === 'confirmation' && <div className="text-center py-8" data-id="element-210">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-211">
                <CheckCircleIcon className="w-10 h-10 text-green-600" data-id="element-212" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-213">
                Order Confirmed!
              </h4>
              <p className="text-gray-600 mb-6" data-id="element-214">
                Thank you for your order. We've sent a confirmation email to{' '}
                {shippingInfo.email}
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 inline-block" data-id="element-215">
                <p className="text-sm text-gray-600 mb-1" data-id="element-216">Order Number</p>
                <p className="text-xl font-bold text-blue-600" data-id="element-217">{orderNumber}</p>
              </div>

              <div className="max-w-md mx-auto text-left space-y-4" data-id="element-218">
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-219">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-220">
                    Order Summary
                  </h5>
                  <p className="text-sm text-gray-600" data-id="element-221">
                    {totalItems} items · ${total.toFixed(2)}
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4" data-id="element-222">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-223">
                    Shipping To
                  </h5>
                  <p className="text-sm text-gray-600" data-id="element-224">
                    {shippingInfo.fullName}
                    <br data-id="element-225" />
                    {shippingInfo.address}
                    <br data-id="element-226" />
                    {shippingInfo.city}, {shippingInfo.state}{' '}
                    {shippingInfo.zipCode}
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4" data-id="element-227">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-228">
                    Estimated Delivery
                  </h5>
                  <p className="text-sm text-gray-600" data-id="element-229">5-7 business days</p>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        {currentStep !== 'confirmation' && <div className="border-t border-gray-200 p-6" data-id="element-230">
            <button onClick={() => {
          if (currentStep === 'review') setCurrentStep('shipping');else if (currentStep === 'shipping' && isShippingValid) setCurrentStep('payment');else if (currentStep === 'payment' && isPaymentValid) handlePlaceOrder();
        }} disabled={currentStep === 'shipping' && !isShippingValid || currentStep === 'payment' && !isPaymentValid} className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors shadow-sm" data-id="element-231">
              {currentStep === 'review' && 'Continue to Shipping'}
              {currentStep === 'shipping' && 'Continue to Payment'}
              {currentStep === 'payment' && `Pay $${total.toFixed(2)}`}
            </button>
          </div>}

        {currentStep === 'confirmation' && <div className="border-t border-gray-200 p-6" data-id="element-232">
            <button onClick={handleClose} className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors" data-id="element-233">
              Continue Shopping
            </button>
          </div>}
      </div>
    </div>;
}